import { authApi } from "@api";
import { envConfig } from "@configs/env";
import { DEFAULT_ERROR_MESSAGE, ValidatorSchema } from "@constants";
import { AppDispatch, RootState } from "@stores";
import auth from "@stores/auth";
import {
  APIErrorCodes,
  AuthToken,
  LoginRequest,
  MemberSettingAuthMode,
  TFunction,
  delay,
  standardizePhoneNumber,
  useCallbackRef,
  useHandleApiResponse,
} from "kz-ui-sdk";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useMemberSettings } from "./useMemberSettings";

type GrantType = "password" | "otp";

const MAX_FAILED_ATTEMPTS: Record<MemberSettingAuthMode, number> = {
  [MemberSettingAuthMode.UsernamePassword]: 5,
  [MemberSettingAuthMode.PhonePin]: 2,
};

interface UseLoginSubmitProps {
  /**
   * Trigger forgot secret after failed attempts
   */
  onForgotSecret?: () => void;

  /**
   * Trigger on validation error or response error
   */
  onLoginError?: (message: string) => void;

  /**
   * Trigger on login success
   */
  onLoginSuccess?: (token?: AuthToken) => void;

  /**
   * Trigger if account is locked
   */
  onAccountLocked?: (isLocked: boolean) => void;

  /**
   * Defer update token
   */
  deferUpdateToken?: boolean;

  /**
   * Config a list of error codes that will be toast error
   */
  toastErrorCodes?: string[];

  /**
   * Config list of error codes that will be set inline error message
   */
  inlineErrorCodes?: string[];

  /**
   * Config if default error message should be toast error
   */
  toastDefaultError?: boolean;

  /**
   * Set inline login error message
   */
  setInlineError?: (error: string) => void;
}

const buildLoginClientId = (clientId: string, authMode: MemberSettingAuthMode, phonePrefix: string) => {
  switch (authMode) {
    case MemberSettingAuthMode.UsernamePassword:
      return clientId.toLowerCase();
    case MemberSettingAuthMode.PhonePin:
      return standardizePhoneNumber(clientId, phonePrefix);
  }
};

const getInvalidCredentialsMessage = (authMode: MemberSettingAuthMode, t: TFunction) => {
  switch (authMode) {
    case MemberSettingAuthMode.UsernamePassword:
      return t("username or password is incorrect. click on forgot password below.");
    case MemberSettingAuthMode.PhonePin:
      return t("Incorrect PIN. Please try again.");
  }
};

export const useLoginSubmit = ({
  onForgotSecret,
  onLoginError: onError,
  onLoginSuccess,
  onAccountLocked,
  deferUpdateToken,
  toastErrorCodes = [
    APIErrorCodes.Auth.AccountClosed,
    APIErrorCodes.Auth.AccountFrozen,
    APIErrorCodes.Auth.AccountSuspended,
    APIErrorCodes.Auth.CredentialsTempLocked,
    APIErrorCodes.Auth.CredentialsTempBlocked,
  ],
  toastDefaultError = true,
  setInlineError,
  inlineErrorCodes = [APIErrorCodes.Auth.InvalidCredentials, APIErrorCodes.Member.NotFound],
}: UseLoginSubmitProps) => {
  const { authMode, phonePrefix } = useMemberSettings();
  const [triggerLogin, { isLoading: isLoggingIn, isSuccess: isLoginSuccess, data: loginData }] =
    authApi.useLoginMutation();
  const { handleApiResponse } = useHandleApiResponse({ toast });
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const failedAttempts = useRef<number>(0);
  const [isError, setIsError] = useState<boolean>(false);
  const profile = useSelector((state: RootState) => state.auth.self);
  const cbRefOnLoginSuccess = useCallbackRef(onLoginSuccess);
  const refAccountLockedTimer = useRef<NodeJS.Timeout | null>(null);

  const isAuthenticated = !!profile;

  const validateSecret = useCallback(
    (secret: string, grantType: GrantType) => {
      switch (authMode) {
        case MemberSettingAuthMode.UsernamePassword:
          if (grantType === "otp") {
            return ValidatorSchema.otp.safeParse(secret);
          }
          return ValidatorSchema.loginPassword.safeParse(secret);
        case MemberSettingAuthMode.PhonePin:
          return ValidatorSchema.loginPin.safeParse(secret);
      }
    },
    [authMode],
  );

  const displayError = useCallback(
    (error: string, message: string) => {
      if (inlineErrorCodes.includes(error)) {
        setInlineError?.(message);
      }
      if (toastErrorCodes.includes(error)) {
        toast.error(message);
      }
      onError?.(error);
    },
    [inlineErrorCodes, setInlineError, onError, toastErrorCodes],
  );

  const increaseFailedAttempts = useCallback(() => {
    if (!authMode) return;
    failedAttempts.current = failedAttempts.current + 1;
    if (failedAttempts.current >= MAX_FAILED_ATTEMPTS[authMode]) {
      onForgotSecret?.();
      // reset counter
      failedAttempts.current = 0;
    }
  }, [authMode, onForgotSecret]);

  useEffect(() => {
    if (isAuthenticated && loginData) {
      cbRefOnLoginSuccess?.(loginData);
    }
  }, [isAuthenticated, loginData, cbRefOnLoginSuccess]);

  /**
   * Submit login request
   * @param clientId - Standardized phone number or Username
   * @param clientSecret - OTP or Password
   * @param grantType - "password" or "otp"
   * @returns {Promise<LoginResponse>}
   */
  const handleLogin = useCallback(
    async (clientId: string, clientSecret: string, grantType: GrantType = "password") => {
      if (!authMode || !phonePrefix) return;
      // reset error message
      setInlineError?.("");

      if (!validateSecret(clientSecret, grantType)?.success) {
        displayError(APIErrorCodes.Auth.InvalidCredentials, getInvalidCredentialsMessage(authMode, t));
        increaseFailedAttempts();
        return;
      }

      const reqClientId = buildLoginClientId(clientId, authMode, phonePrefix);
      const reqClientSecret = clientSecret;

      const loginRequest = buildLoginRequest(reqClientId, reqClientSecret, grantType);

      const response = await triggerLogin(loginRequest);

      handleApiResponse(response, {
        toastError: false,
        onSuccess: () => {
          setIsError(false);
          if (!response.data) return;
          dispatch(auth.slice.actions.resetExpiredAt());

          const updateToken = () => {
            dispatch(auth.slice.actions.updateAccessToken(response.data));
          };

          if (deferUpdateToken) {
            delay(1000).then(() => updateToken());
          } else {
            updateToken();
          }
        },
        onError: (error) => {
          setIsError(true);
          const errorMsg = error?.data?.error?.message;
          if (!errorMsg) {
            if (toastDefaultError) {
              toast.error(t(DEFAULT_ERROR_MESSAGE));
            }
            onError?.(DEFAULT_ERROR_MESSAGE);
            return;
          }

          switch (errorMsg) {
            // wrong credentials
            case APIErrorCodes.Auth.InvalidCredentials:
              displayError(errorMsg, getInvalidCredentialsMessage(authMode, t));
              increaseFailedAttempts();
              return;
            // account suspended, closed, frozen
            case APIErrorCodes.Auth.AccountSuspended:
            case APIErrorCodes.Auth.AccountClosed:
            case APIErrorCodes.Auth.AccountFrozen:
              displayError(errorMsg, t("sign in failed, please contact customer service"));
              return;
            // credentials temp locked, blocked, display time remaining
            case APIErrorCodes.Auth.CredentialsTempLocked:
            case APIErrorCodes.Auth.CredentialsTempBlocked: {
              const remainingTime = error?.data?.error?.errors?.remaining as unknown as number;
              if (remainingTime) {
                const minutes = Math.floor(remainingTime / 60);
                const seconds = remainingTime % 60;
                displayError(errorMsg, t("try again in {{minutes}} and {{seconds}}.", { minutes, seconds }));
                onAccountLocked?.(true);

                // release lock
                if (refAccountLockedTimer.current) {
                  clearTimeout(refAccountLockedTimer.current);
                }
                refAccountLockedTimer.current = setTimeout(() => {
                  onAccountLocked?.(false);
                }, remainingTime * 1000);
              }

              return;
            }
            case APIErrorCodes.Member.NotFound:
              displayError(errorMsg, t("number not registered. click register tab to sign up"));
              return;
            default:
              if (toastDefaultError) {
                toast.error(t(errorMsg));
              }
              onError?.(errorMsg);
          }
        },
      });

      return response;
    },
    [
      authMode,
      phonePrefix,
      onError,
      validateSecret,
      triggerLogin,
      toastDefaultError,
      t,
      increaseFailedAttempts,
      dispatch,
      deferUpdateToken,
      handleApiResponse,
      displayError,
      setInlineError,
      onAccountLocked,
    ],
  );

  // make form loading until next step
  const isLoading = isLoggingIn || isLoginSuccess;

  // clear timeout
  useEffect(() => {
    return () => {
      if (refAccountLockedTimer.current) {
        clearTimeout(refAccountLockedTimer.current);
      }
    };
  }, []);

  return {
    handleLogin,
    isLoading,
    isLoginSuccess,
    isLoggingIn,
    isError,
  };
};

const buildLoginRequest = (clientId: string, clientSecret: string, grantType: GrantType): LoginRequest => {
  switch (grantType) {
    case "otp":
      return {
        phone: clientId,
        code: clientSecret,
        grant_type: "otp",
        account: envConfig.accountId,
      };
    case "password":
    default:
      return {
        client_id: clientId,
        client_secret: clientSecret,
        grant_type: "password",
        account: envConfig.accountId,
      };
  }
};
